import { getIn } from 'formik'
import i18n from 'i18next'
import { Fragment, useContext } from 'react'
import Select from 'react-select'
import { Col, Input, Label, Row } from 'reactstrap'
import exportApi from '../../api/export/hooks'
import { WEBSITE_ATTRIBUTES } from '../../common/constants'
import { propertyExportFields } from '../../common/forms/generalRealEstate/fields'
import FormMainContainer from '../Common/FormMainContainer'
import FormSideContainer from '../Common/FormSideContainer'
import PropertyFormExportContext from './PropertyFormExportContext'

const PropertyFormExportTab = ({ children, ...props }) => {
    return (
        <PropertyFormExportContext.Provider
            value={{
                tabs: props.tabs,
                activeTab: props.activeTab,
                handleTabChange: props.handleTabChange,
                formik: props.formik,
                subform: props.subform,
            }}
        >
            <Row>{children}</Row>
        </PropertyFormExportContext.Provider>
    )
}

export const PropertyFormExportTabSide = () => {
    return <FormSideContainer></FormSideContainer>
}

export const PropertyFormExportTabMain = () => {
    const { tabs, activeTab, handleTabChange, formik } = useContext(PropertyFormExportContext)

    const { data: portals = [] } = exportApi.GetPortals.useQuery()
    const { data: websites = [] } = exportApi.GetWebsites.useQuery()

    const portalValues = getIn(formik?.values?.export, propertyExportFields.PROPERTY_PORTAL_ID)
    const websiteValues = getIn(formik?.values?.export, propertyExportFields.PROPERTY_WEBSITE_ID)

    const isAllPortalsChecked = portals.length > 0 && portals.every((portal) => portalValues?.includes(portal.id))

    const checkedAllPortals = (e) => {
        formik.setFieldValue(
            `export.${propertyExportFields.PROPERTY_PORTAL_ID}`,
            e.target.checked ? portals.map((x) => x.id) : []
        )
    }

    const addCustomField = (customField, portal) => {
        const customFieldObj = { customField, portal }
        const currentValue = formik.values?.export?.propertyPortalCustomValue || []

        // currently only one portal has custom fields so this returns empty array
        // but might be more portals with custom fields in future
        const otherPortalsSelections = currentValue.filter((obj) => obj.portal !== portal)

        const newPortalCustomValue = customField ? [...otherPortalsSelections, customFieldObj] : otherPortalsSelections

        formik.setFieldValue('export.propertyPortalCustomValue', newPortalCustomValue)
    }

    const handleWebsiteCheck = (existingIndex, website_id, attribute) => {
        if (existingIndex !== -1) {
            return websiteValues.map((value, index) => (index === existingIndex ? { ...value, [attribute]: 1 } : value))
        } else {
            const newValue = {
                website_id,
                [attribute]: 1,
            }
            return [...websiteValues, newValue]
        }
    }

    const handleWebsiteUncheck = (existingIndex, website_id, attribute, CLONE_WEBSITE_ATTRIBUTES) => {
        const currentWebsiteObj = websiteValues[existingIndex]
        const updatedWebsiteObj = { ...currentWebsiteObj, [attribute]: 0 }

        const attributesObj = Object.keys(currentWebsiteObj).reduce((acc, curr) => {
            const isWebsiteAttribute = CLONE_WEBSITE_ATTRIBUTES.some((cwb) => cwb.attribute === curr)

            if (isWebsiteAttribute) {
                acc[curr] = updatedWebsiteObj[curr]
            }

            return acc
        }, {})

        const isAllUnchecked = Object.values(attributesObj).every((value) => value !== 1)

        if (isAllUnchecked && !currentWebsiteObj.id) {
            return websiteValues.filter((value) => value.website_id !== website_id)
        } else {
            return websiteValues.map((value, index) => (index === existingIndex ? { ...value, [attribute]: 0 } : value))
        }
    }

    return (
        <FormMainContainer ainContainer tabs={tabs} activeTab={activeTab} handleTabChange={handleTabChange}>
            <Row>
                <Col sm={12} md={6}>
                    <p>{i18n.t('form.property.tab.export.portals')}</p>
                    <div className="form-check mb-3">
                        <Input
                            type="checkbox"
                            className="form-check-input"
                            name="portals-check-all"
                            id="portalsCheckBoxAll"
                            onChange={checkedAllPortals}
                            checked={isAllPortalsChecked}
                        />
                        <Label className="form-check-label" for="portalsCheckBoxAll">
                            {i18n.t('form.property.tab.export.checkboxAll')}
                        </Label>
                    </div>
                    {portals && portals.length
                        ? portals.map((portal) => {
                              const optionsCustomField = JSON.parse(portal.portalCustomField)
                              const formikSelected = formik?.values?.export?.propertyPortalCustomValue?.filter((item) => item.portal === portal.id)[0]

                              return (
                                  <div key={portal.id} className="form-check mb-2 d-flex w-100">
                                      <div className="d-flex align-items-center my-2">
                                          <Input
                                              type="checkbox"
                                              className="form-check-input portalCheckBox"
                                              id={`portal-checkbox-${portal.id}`}
                                              name={`portal-checkbox-${portal.id}`}
                                              value={portal.id.toString()}
                                              checked={portalValues?.includes(portal.id) || false}
                                              onChange={(e) => {
                                                  let newvalues
                                                  if (e.target.checked) {
                                                      newvalues = [...portalValues, portal.id]
                                                  } else {
                                                      newvalues = [...portalValues.filter((x) => x !== portal.id)]
                                                  }
                                                  formik.setFieldValue(`export.${propertyExportFields.PROPERTY_PORTAL_ID}`, [...new Set(newvalues)])
                                              }}
                                          />
                                          <Label className="form-check-label ms-2" for={`portal-checkbox-${portal.id}`}>
                                              {portal.title} {portal.count ? `(${portal.count})` : ''}
                                          </Label>
                                          <div className="d-flex ms-3">
                                              {portal.portalCustomField && (
                                                  <Select
                                                      key={portal.id}
                                                      inputId={`portal-checkbox-${portal.id}`}
                                                      onChange={(e) => {
                                                          addCustomField(e?.value, portal.id)
                                                      }}
                                                      isDisabled={!portalValues?.includes(portal.id)}
                                                      options={optionsCustomField}
                                                      placeholder={i18n.t('form.property.tab.export.select.label')}
                                                      value={optionsCustomField?.filter((item) => item.value === formikSelected?.customField)}
                                                      isClearable={true}
                                                  />
                                              )}
                                          </div>
                                      </div>
                                  </div>
                              )
                          })
                        : null}
                </Col>
                <Col sm={12} md={6}>
                    <p>{i18n.t('app.common.web')}</p>
                    <Row>
                        <Col>
                            {websites && websites.length
                                ? websites.map(({ id: website_id, title, supportsIncognito, ...attributes }, i) => {
                                      const CLONE_WEBSITE_ATTRIBUTES = [...WEBSITE_ATTRIBUTES]
                                      supportsIncognito === 0 && CLONE_WEBSITE_ATTRIBUTES.splice(1, 1)

                                      return (
                                          <Fragment key={website_id}>
                                              <p>{title}</p>
                                              {CLONE_WEBSITE_ATTRIBUTES.map(({ attribute, name }) => {
                                                  const elementId = `website-checkbox-${website_id}-${attribute}`
                                                  const currentWebsiteObj = websiteValues?.find((obj) => obj?.website_id === website_id)
                                                  const isChecked = currentWebsiteObj?.[attribute] === 1

                                                  return (
                                                      <div key={attribute} className="form-check mb-2">
                                                          <Input
                                                              type="checkbox"
                                                              className="form-check-input"
                                                              id={elementId}
                                                              name={attribute}
                                                              value={attributes[attribute]}
                                                              checked={isChecked}
                                                              onChange={(e) => {
                                                                  const existingIndex = websiteValues.findIndex(
                                                                      (value) => value.website_id === website_id
                                                                  )

                                                                  const newValues = e.target.checked
                                                                      ? handleWebsiteCheck(existingIndex, website_id, attribute)
                                                                      : handleWebsiteUncheck(
                                                                            existingIndex,
                                                                            website_id,
                                                                            attribute,
                                                                            CLONE_WEBSITE_ATTRIBUTES
                                                                        )

                                                                  formik.setFieldValue(
                                                                      `export.${propertyExportFields.PROPERTY_WEBSITE_ID}`,
                                                                      newValues
                                                                  )
                                                              }}
                                                          />
                                                          <Label className="form-check-label" for={elementId}>
                                                              {i18n.t(name)}
                                                          </Label>
                                                      </div>
                                                  )
                                              })}
                                          </Fragment>
                                      )
                                  })
                                : null}
                        </Col>
                    </Row>
                </Col>
            </Row>
        </FormMainContainer>
    )
}

export default PropertyFormExportTab
